import { useContext, useCallback, useEffect } from 'react'
import styled from '@emotion/styled'
import { t } from '@lingui/core/macro'
import { useQuery } from '@tanstack/react-query'
import { api } from 'api'
import {
  BITLY_ORG_KEY,
  BitlyOrgTitle,
  BitlyGroupTitle,
  BITLY_GROUP_KEY,
  BITLY_SHORTENED_URL,
  BitlyDomainTitle,
  BitlyWarning,
  BitlyQRCode,
} from './components'
import { templateCopyContext } from '../../context'
import {
  AccountSelectionBuilder,
  CustomFieldBuilder,
  DropdownBuilder,
  FieldMappingBuilder,
  IntegrationFormField,
  IntegrationFormManager,
  IntegrationNameBuilder,
} from '../components'

const FullWidth = styled.div`
  width: 100%;
`

interface BitlyOrg {
  label: string
  value: string
  planType: string
}

interface BitlyGroup {
  label: string
  value: string
  urls: string[]
}

interface BitlyApiResponse {
  id: string
  name: string
  planType: string
  orgId: string
  urls: string[]
}

interface BitlyProps {
  assignment: any
}

interface TemplateCopyContextType {
  onChange: (callback: (old: Record<string, any>) => Record<string, any>) => void
  template: any
}

export const Bitly = ({ assignment }: BitlyProps) => {
  const { onChange } = useContext(templateCopyContext) as TemplateCopyContextType

  function customBitlyIsEnabled() {
    // using this function to determine the status of custom_bitlink_enabled because the metadata table stores the boolean value as a string
    if (assignment.metadata.custom_bitlink_enabled) {
      return !(assignment.metadata.custom_bitlink_enabled === '0')
    }
    return false
  }

  const { data: orgs = [], isLoading: loadingOrgs } = useQuery({
    queryKey: ['bitly', assignment.connector_id, 'organizations'],
    queryFn: () =>
      api.get<BitlyApiResponse[]>(
        `/api/v1/integrations/bitly/organizations/${assignment.connector_id}`,
      ),
    select: ({ data }) =>
      data.map((d): BitlyOrg => ({ value: d.id, label: d.name, planType: d.planType })),
    enabled: !!assignment.connector_id,
  })

  const { data: groups = {}, isLoading: loadingGroups } = useQuery({
    queryKey: ['bitly', assignment.connector_id, 'groups'],
    queryFn: () =>
      api.get<BitlyApiResponse[]>(`/api/v1/integrations/bitly/groups/${assignment.connector_id}`),
    select: ({ data }): Record<string, BitlyGroup[]> =>
      data.reduce(
        (acc, d) => ({
          ...acc,
          [d.orgId]: [...(acc[d.orgId] || []), { label: d.name, value: d.id, urls: d.urls }],
        }),
        {},
      ),
    enabled: !!assignment.connector_id,
  })

  const formatBitlyIntegration = () => {
    onChange(old => ({
      ...old,
      [assignment.id]: {
        ...old[assignment.id],
        mapping_fields: [],
      },
    }))
  }

  const onBitlyMetadataChange = useCallback(
    value => {
      onChange(old => ({
        ...old,
        [assignment.id]: {
          ...old[assignment.id],
          metadata: {
            ...old[assignment.id].metadata,
            ...value,
          },
        },
      }))
    },
    [onChange, assignment],
  )

  const onBitlyMappingChange = useCallback(
    value => {
      onChange(old => ({
        ...old,
        [assignment.id]: {
          ...old[assignment.id],
          mapping_fields: value,
        },
      }))
    },
    [assignment, onChange],
  )

  useEffect(() => {
    if (!assignment.mapping_fields) {
      formatBitlyIntegration()
    }

    if (assignment?.mapping_fields?.length && !customBitlyIsEnabled()) {
      onBitlyMetadataChange({ custom_bitlink_enabled: true })
    }
  }, [])

  useEffect(() => {
    if (Object.keys(assignment.metadata).find(m => m === 'custom_bitlink_enabled')) {
      if (customBitlyIsEnabled() && assignment.mapping_fields?.length) {
        return
      }
      onBitlyMappingChange(
        customBitlyIsEnabled()
          ? [
              {
                cvFieldId: '',
                required: true,
                fieldName: 'custom_bitlink',
                type: 'integration_field',
              },
            ]
          : [],
      )
    }
  }, [assignment.metadata.custom_bitlink_enabled])

  const customBitlyEnabled = customBitlyIsEnabled() || false

  const domains =
    groups[assignment.metadata[BITLY_ORG_KEY]]
      ?.find(g => g.value === assignment.metadata.bitly_group_id)
      ?.urls.map(u => ({ label: u, value: u })) ?? []

  const integrationNameField = new IntegrationNameBuilder().build()
  const accountSelectionField = new AccountSelectionBuilder().resetOnChange(true).build()
  const orgDropdownField = new DropdownBuilder()
    .placement(2)
    .options(orgs)
    .selected(assignment.metadata[BITLY_ORG_KEY] || null)
    .title(BitlyOrgTitle)
    .label(t`Organization`)
    .waitingOnDependancy(assignment.connector_id == null)
    .dependancyTitle(t`Account Selection`)
    .isLoading(loadingOrgs)
    .metadataKey(BITLY_ORG_KEY)
    .build()
  const groupDropdownField = new DropdownBuilder()
    .placement(3)
    .options(groups[assignment.metadata[BITLY_ORG_KEY]] || [])
    .selected(assignment.metadata[BITLY_GROUP_KEY] || null)
    .title(BitlyGroupTitle)
    .label(t`Group`)
    .waitingOnDependancy(assignment.metadata[BITLY_ORG_KEY] == null)
    .dependancyTitle(BitlyOrgTitle)
    .isLoading(loadingGroups)
    .metadataKey(BITLY_GROUP_KEY)
    .build()
  const domainDropdownField = new DropdownBuilder()
    .placement(4)
    .options(domains)
    .selected(assignment.metadata[BITLY_SHORTENED_URL] || [])
    .title(BitlyDomainTitle)
    .label(t`Shortened URL`)
    .waitingOnDependancy(assignment.metadata[BITLY_GROUP_KEY] == null)
    .dependancyTitle(BitlyGroupTitle)
    .isLoading(loadingGroups)
    .metadataKey(BITLY_SHORTENED_URL)
    .build()
  const customField = new CustomFieldBuilder()
    .placement(5)
    .customField(<BitlyWarning customBitlyEnabled={customBitlyEnabled} assignment={assignment} />)
    .build()
  const fieldMapping = new FieldMappingBuilder()
    .placement(6)
    .isHidden(!customBitlyEnabled)
    .integrationTitle(t`Bitly`)
    .mappingType('supplemental')
    .build()
  const qrcodeFields = new CustomFieldBuilder()
    .placement(7)
    .customField(<BitlyQRCode assignment={assignment} onMetadataChange={onBitlyMetadataChange} />)
    .build()

  const BitlyFormFields: IntegrationFormField[] = [
    integrationNameField,
    accountSelectionField,
    orgDropdownField,
    groupDropdownField,
    domainDropdownField,
    customField,
    qrcodeFields,
    fieldMapping,
  ]

  return (
    <FullWidth>
      <IntegrationFormManager
        integrationType="bitly"
        integrationAssignment={assignment}
        integrationFields={BitlyFormFields}
      />
    </FullWidth>
  )
}
